import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useGetSessionUserQuery } from '../../redux/api/auth';
import { useAppSelector } from '../../redux/hooks';
import { isAuthRedirectInProgress, isPublicAuthRoute } from '../auth/authRedirect';

const buildUsername = (input: {
  userName?: string | null;
  email?: string | null;
  name?: string | null;
}) => {
  if (input.userName?.trim()) return input.userName.trim();
  if (input.email?.includes('@')) return input.email.split('@')[0].trim();
  if (input.name?.trim()) return input.name.trim();
  return undefined;
};

const clearUserContext = () => {
  Sentry.setUser(null);
  Sentry.setTag('authenticated', 'false');
  Sentry.setTag('username', 'anonymous');
  Sentry.setTag('user_id', 'anonymous');
};

export const SentryUserSync = () => {
  const shouldSkipSessionQuery = isPublicAuthRoute() || isAuthRedirectInProgress();
  const { data: currentUser } = useGetSessionUserQuery(undefined, {
    skip: shouldSkipSessionQuery,
  });
  const selectedPos = useAppSelector((state) => state.auth.selectedPos);

  useEffect(() => {
    if (!currentUser) {
      clearUserContext();
      return;
    }

    const username = buildUsername({
      userName: currentUser.email,
      email: currentUser.email,
      name: currentUser.name,
    });

    Sentry.setUser({
      id: currentUser.user_id ?? currentUser.sub,
      email: currentUser.email,
      username,
      name: currentUser.name,
    });

    Sentry.setTag('authenticated', 'true');
    Sentry.setTag('username', username ?? 'unknown');
    Sentry.setTag('user_id', currentUser.user_id ?? currentUser.sub ?? 'unknown');
    Sentry.setTag('organization_id', currentUser.organization_id ?? 'none');
    Sentry.setTag('is_dealer', String(!!currentUser.dealer));
    Sentry.setTag('is_system_admin', String(!!currentUser.system_admin));
    Sentry.setTag('point_of_sale_id', selectedPos?.id ?? 'none');
    Sentry.setTag('point_of_sale_name', selectedPos?.name ?? 'none');

    Sentry.setContext('auth', {
      sub: currentUser.sub,
      userId: currentUser.user_id,
      email: currentUser.email,
      name: currentUser.name,
      organizationId: currentUser.organization_id,
      dealer: !!currentUser.dealer,
      systemAdmin: !!currentUser.system_admin,
      seller: !!currentUser.seller,
    });

    Sentry.setContext('point_of_sale', {
      id: selectedPos?.id,
      name: selectedPos?.name,
      marketId: selectedPos?.marketId,
      marketCode: selectedPos?.marketCode,
      companyTaxId: selectedPos?.companyTaxId,
      organizationId: selectedPos?.organizationId,
      isDealer: !!selectedPos?.isDealer,
      isDarkStore: !!selectedPos?.isDarkStore,
    });
  }, [currentUser, selectedPos]);

  return null;
};
